import { ref, computed, type Ref } from 'vue';
import type { TableElement } from '../model/form.types';
import { useTableEditorStore } from '../model/table-editor.store';
import { TABLE_CONSTRAINTS, RESIZE_THROTTLE_MS } from '../lib/table-constants';
import { throttle } from '../lib/throttle';
import { getResolvedColumnWidthsPx } from '../lib/table-column-utils';
import { trackReactiveDep } from '../lib/track-reactive-dep';

const areArraysIdentical = (a: number[], b: number[]) =>
  a.length === b.length && a.every((val, i) => val === b[i]);

export interface ResizeState {
  type: 'column' | 'row';
  index: number;
  startPos: number;
  startSize: number;
  combinedWidth?: number;
  combinedHeight?: number;
  currentWidths?: number[];
  currentHeights?: number[];
}

export function useTableResize(
  tableElement: Ref<TableElement>,
  isEditable: Ref<boolean>,
  gridRef?: Ref<HTMLElement | null>,
  layoutVersion?: Ref<number>
) {
  const tableEditorStore = useTableEditorStore();
  const resizing = ref<ResizeState | null>(null);

  const throttledUpdateColumnWidths = throttle((tableId: string, widths: number[]) => {
    tableEditorStore.updateTableColumnWidths(tableId, widths);
  }, RESIZE_THROTTLE_MS);

  const throttledUpdateRowHeights = throttle((tableId: string, heights: number[]) => {
    tableEditorStore.updateTableRowHeights(tableId, heights);
  }, RESIZE_THROTTLE_MS);

  const resizeTooltip = computed(() => {
    if (!resizing.value) return null;

    if (resizing.value.type === 'column') {
      const widths = resizing.value.currentWidths ?? getColumnWidthsForResize();
      if (!widths || resizing.value.index >= widths.length) return null;
      return {
        text: `${Math.round(widths[resizing.value.index])}px | ${Math.round(widths[resizing.value.index + 1])}px`,
        label: 'Largura das colunas',
      };
    } else {
      const heights = tableElement.value.properties.rowHeights;
      if (!heights || resizing.value.index >= heights.length) return null;
      return {
        text: `${Math.round(heights[resizing.value.index])}px`,
        label: 'Altura da linha',
      };
    }
  });

  const isValidColumnSize = (size: number | undefined): size is number => {
    return typeof size === 'number' && size > 0 && Number.isFinite(size);
  };

  const initializeColumnWidths = (totalColumns: number, event?: MouseEvent): number[] => {
    const gridElement = getGridElement(event);

    if (gridElement && gridElement.clientWidth > 0) {
      const totalWidth = gridElement.clientWidth;
      const equalWidth = Math.floor(totalWidth / totalColumns);
      return new Array<number>(totalColumns).fill(equalWidth);
    }

    return new Array<number>(totalColumns).fill(TABLE_CONSTRAINTS.DEFAULT_COLUMN_WIDTH);
  };

  const getGridElement = (event?: MouseEvent): HTMLElement | null => {
    const eventTarget = event?.target as HTMLElement | undefined;

    return (
      gridRef?.value ||
      eventTarget?.closest('.table-grid-wrapper')?.querySelector('.table-grid') ||
      null
    );
  };

  const resolveColumnWidths = (totalColumns: number, event?: MouseEvent): number[] => {
    const currentColumnWidths = tableElement.value.properties.columnWidths;
    const gridElement = getGridElement(event);

    if (currentColumnWidths?.length !== totalColumns) {
      return initializeColumnWidths(totalColumns, event);
    }

    return getResolvedColumnWidthsPx(
      currentColumnWidths,
      totalColumns,
      gridElement?.clientWidth ?? 0
    );
  };

  const startColumnResize = (colIndex: number, event: MouseEvent) => {
    if (!isEditable.value) return;
    event.preventDefault();
    event.stopPropagation();

    const totalColumns = tableElement.value.properties.columns;
    const hasStoredWidths =
      tableElement.value.properties.columnWidths?.length === totalColumns;
    // Seed from the real rendered layout: `fr` columns grow to fit content beyond their
    // stored proportional widths, so measuring keeps the handle in place on click instead
    // of snapping to the (smaller) proportional boundary before the drag starts.
    const columnWidths =
      measureRenderedColumnWidths(totalColumns) ?? resolveColumnWidths(totalColumns, event);

    if (!hasStoredWidths) {
      tableEditorStore.updateTableColumnWidths(tableElement.value.id, columnWidths);
    }

    if (colIndex < 0 || colIndex >= totalColumns - 1) {
      return;
    }

    const leftColSize = columnWidths[colIndex];
    const rightColSize = columnWidths[colIndex + 1];

    if (!isValidColumnSize(leftColSize) || !isValidColumnSize(rightColSize)) {
      return;
    }

    const combinedWidth = leftColSize + rightColSize;

    resizing.value = {
      type: 'column',
      index: colIndex,
      startPos: event.clientX,
      startSize: leftColSize,
      combinedWidth,
      currentWidths: [...columnWidths],
    };
  };

  const startRowResize = (rowIndex: number, event: MouseEvent) => {
    if (!isEditable.value) return;
    event.preventDefault();
    event.stopPropagation();

    let rowHeights = tableElement.value.properties.rowHeights;

    if (!rowHeights || rowHeights.length !== tableElement.value.properties.rows) {
      const gridElement = (event.target as HTMLElement)
        .closest('.table-grid-wrapper')
        ?.querySelector('.table-grid');
      if (gridElement) {
        const totalHeight = gridElement.clientHeight;
        const equalHeight = Math.floor(totalHeight / tableElement.value.properties.rows);
        rowHeights = new Array<number>(tableElement.value.properties.rows).fill(
          Math.min(equalHeight, TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT)
        );
        tableEditorStore.updateTableRowHeights(tableElement.value.id, rowHeights);
      } else {
        return;
      }
    }

    if (!rowHeights || rowHeights.length === 0 || rowIndex >= rowHeights.length) {
      return;
    }

    const clampedHeights = rowHeights.map((h: number) =>
      Math.max(TABLE_CONSTRAINTS.MIN_ROW_HEIGHT, Math.min(TABLE_CONSTRAINTS.MAX_ROW_HEIGHT, h))
    );

    if (!areArraysIdentical(clampedHeights, rowHeights)) {
      tableEditorStore.updateTableRowHeights(tableElement.value.id, clampedHeights);
      rowHeights = clampedHeights;
    }

    const topRowSize = rowHeights[rowIndex];

    resizing.value = {
      type: 'row',
      index: rowIndex,
      startPos: event.clientY,
      startSize: topRowSize,
      combinedHeight: 0,
      currentHeights: [...rowHeights],
    };
  };

  const handleColumnResize = (
    index: number,
    startPos: number,
    startSize: number,
    combinedWidth: number,
    event: MouseEvent,
    currentWidths: number[]
  ) => {
    const delta = event.clientX - startPos;
    if (!currentWidths || index + 1 >= currentWidths.length) return;

    const minWidth = TABLE_CONSTRAINTS.MIN_COLUMN_WIDTH;
    let newLeftWidth = startSize + delta;

    newLeftWidth = Math.max(minWidth, Math.min(combinedWidth - minWidth, newLeftWidth));
    const newRightWidth = combinedWidth - newLeftWidth;

    const updatedWidths = [...currentWidths];
    updatedWidths[index] = Math.round(newLeftWidth);
    updatedWidths[index + 1] = Math.round(newRightWidth);

    if (resizing.value) {
      resizing.value.currentWidths = updatedWidths;
    }

    throttledUpdateColumnWidths(tableElement.value.id, updatedWidths);
  };

  const handleSingleRowResize = (
    index: number,
    startSize: number,
    delta: number,
    currentHeights: number[]
  ) => {
    const minHeight = TABLE_CONSTRAINTS.MIN_ROW_HEIGHT;
    const maxHeight: number = TABLE_CONSTRAINTS.MAX_ROW_HEIGHT;

    // Rows may grow past the current page boundary: the table now breaks across
    // pages instead of being clamped to a single page.
    let newHeight = startSize + delta;

    newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

    const updatedHeights = [...currentHeights];
    updatedHeights[index] = Math.round(newHeight);

    if (resizing.value?.type === 'row') {
      resizing.value.currentHeights = updatedHeights;
    }

    throttledUpdateRowHeights(tableElement.value.id, updatedHeights);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!resizing.value) return;

    const {
      type,
      index,
      startPos,
      startSize,
      combinedWidth,
      currentWidths,
      currentHeights,
    } = resizing.value;

    if (type === 'column' && combinedWidth && currentWidths) {
      handleColumnResize(index, startPos, startSize, combinedWidth, event, currentWidths);
    } else if (type === 'row') {
      const delta = event.clientY - startPos;
      const heights = currentHeights || tableElement.value.properties.rowHeights;
      if (!heights) return;

      handleSingleRowResize(index, startSize, delta, heights);
    }
  };

  const handleMouseUp = () => {
    if (resizing.value) {
      if (resizing.value.type === 'column' && resizing.value.currentWidths) {
        tableEditorStore.updateTableColumnWidths(tableElement.value.id, resizing.value.currentWidths);
      } else if (resizing.value.type === 'row' && resizing.value.currentHeights) {
        tableEditorStore.updateTableRowHeights(tableElement.value.id, resizing.value.currentHeights);
      }
    }
    resizing.value = null;
  };

  const getColumnWidthsForResize = (): number[] | undefined => {
    if (resizing.value?.type === 'column' && resizing.value?.currentWidths) {
      return resizing.value.currentWidths;
    }

    const totalColumns = tableElement.value.properties.columns;
    const columnWidths = tableElement.value.properties.columnWidths;
    if (columnWidths?.length !== totalColumns) {
      return columnWidths;
    }

    return getResolvedColumnWidthsPx(columnWidths, totalColumns, gridRef?.value?.clientWidth ?? 0);
  };

  const calculateOffsetFromWidths = (
    columnWidths: number[],
    colIndex: number
  ): { left: string } => {
    const offset = columnWidths.slice(0, colIndex).reduce((sum, width) => sum + width, 0);
    return { left: `${offset}px` };
  };

  const getCellColSpan = (cell: HTMLElement): number => {
    return Number.parseInt(cell.getAttribute('aria-colspan') ?? '1', 10);
  };

  const findTargetCellForColumn = (targetColIndex: number): HTMLElement | null => {
    if (!gridRef?.value) return null;

    try {
      const allVisibleCells = Array.from(
        gridRef.value.querySelectorAll('.table-cell:not(.hidden)')
      ) as HTMLElement[];

      let minRowIndex = Infinity;
      let targetCell: HTMLElement | null = null;

      for (const cell of allVisibleCells) {
        const cellColIndex = Number.parseInt(cell.dataset.colIndex ?? '-1', 10);
        const cellRowIndex = Number.parseInt(cell.dataset.rowIndex ?? '-1', 10);

        // Skip cells that span columns: their offsetWidth covers multiple columns,
        // so they would yield the far edge of the merged region instead of this
        // column's boundary (mirrors the row logic in useTableRowHeights).
        if (getCellColSpan(cell) > 1) continue;

        if (cellColIndex === targetColIndex && cellRowIndex < minRowIndex) {
          minRowIndex = cellRowIndex;
          targetCell = cell;
        }
      }

      return targetCell && targetCell.offsetWidth > 0 ? targetCell : null;
    } catch (e) {
      console.error('Error calculating column resize handle position:', e);
      return null;
    }
  };

  const measureRenderedColumnWidths = (totalColumns: number): number[] | null => {
    if (!gridRef?.value) return null;

    const leftEdges: number[] = [];
    let lastCell: HTMLElement | null = null;

    for (let col = 0; col < totalColumns; col++) {
      const cell = findTargetCellForColumn(col);
      if (!cell) return null;
      leftEdges.push(cell.offsetLeft);
      lastCell = cell;
    }

    if (!lastCell) return null;

    const rightEdge = lastCell.offsetLeft + lastCell.offsetWidth;
    const widths = leftEdges.map((left, col) =>
      (col + 1 < totalColumns ? leftEdges[col + 1] : rightEdge) - left
    );

    return widths.every((w) => w > 0) ? widths : null;
  };

  const findColumnStartLeft = (colIndex: number): number | null => {
    if (!gridRef?.value) return null;

    try {
      const matchingCells = Array.from(
        gridRef.value.querySelectorAll(
          `.table-cell[data-col-index="${colIndex}"]:not(.hidden)`
        )
      ) as HTMLElement[];

      for (const cell of matchingCells) {
        const cellColIndex = Number.parseInt(cell.dataset.colIndex ?? '-1', 10);
        if (cellColIndex === colIndex && cell.offsetWidth > 0) {
          return cell.offsetLeft;
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const getColumnResizeHandleStyle = (colIndex: number) => {
    const targetColIndex = colIndex - 1;
    const totalColumns = tableElement.value.properties.columns;

    // Registers a reactive dep so the handle re-measures whenever the layout shifts
    // (e.g. a column grows to fit newly added cell content). The value is otherwise unused.
    trackReactiveDep(layoutVersion);

    const targetCell = findTargetCellForColumn(targetColIndex);
    const measuredLeft = targetCell ? targetCell.offsetLeft + targetCell.offsetWidth : null;

    if (resizing.value?.type === 'column' && resizing.value.currentWidths) {
      const liveWidths = resizing.value.currentWidths;
      if (liveWidths.length === totalColumns && liveWidths.every((w) => w > 0)) {
        const liveLeft = Number.parseFloat(calculateOffsetFromWidths(liveWidths, colIndex).left);
        const left = measuredLeft !== null ? Math.max(measuredLeft, liveLeft) : liveLeft;
        return { left: `${left}px` };
      }
    }

    if (measuredLeft !== null) {
      return { left: `${measuredLeft}px` };
    }

    // No standard cell starts at the column on the left of the boundary (e.g. that
    // column is merged across every row); fall back to the left edge of the column
    // on the right of the boundary.
    const rightColumnLeft = findColumnStartLeft(colIndex);
    if (rightColumnLeft !== null) {
      return { left: `${rightColumnLeft}px` };
    }

    // Last resort: derive the boundary from the stored/resolved column widths.
    const columnWidths = getColumnWidthsForResize();
    if (columnWidths?.length === totalColumns && columnWidths.every((w) => w > 0)) {
      return calculateOffsetFromWidths(columnWidths, colIndex);
    }

    return {
      left: `${(colIndex / totalColumns) * 100}%`,
    };
  };

  return {
    resizing,
    resizeTooltip,
    startColumnResize,
    startRowResize,
    handleMouseMove,
    handleMouseUp,
    getColumnResizeHandleStyle,
  };
}
